import { defineComponent as _defineComponent } from 'vue';
import { library } from "@fortawesome/fontawesome-svg-core";
import { faEye } from "@fortawesome/free-regular-svg-icons";
import { faArrowDown, faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { useInfiniteScroll } from "@vueuse/core";
import { BAlert, BBadge, BButton, BListGroup, BListGroupItem } from "bootstrap-vue";
import { storeToRefs } from "pinia";
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router/composables";
import { getData } from "@/components/Grid/configs/invocations";
import { useAnimationFrameResizeObserver } from "@/composables/sensors/animationFrameResizeObserver";
import { useAnimationFrameScroll } from "@/composables/sensors/animationFrameScroll";
import { useHistoryStore } from "@/stores/historyStore";
import { useUserStore } from "@/stores/userStore";
import { useWorkflowStore } from "@/stores/workflowStore";
import Heading from "@/components/Common/Heading.vue";
import LoadingSpan from "@/components/LoadingSpan.vue";
import ScrollToTopButton from "@/components/ToolsList/ScrollToTopButton.vue";
import UtcDate from "@/components/UtcDate.vue";
export default /*#__PURE__*/ _defineComponent({
    __name: 'InvocationScrollList',
    props: {
        inPanel: { type: Boolean, default: false },
        limit: { default: 20 }
    },
    emits: ["invocation-clicked"],
    setup(__props, { emit }) {
        const props = __props;
        library.add(faEye, faArrowDown, faInfoCircle);
        const stateClasses = {
            ready: "waiting",
            scheduled: "ok",
            failed: "error",
        };
        const busy = ref(false);
        const errorMessage = ref("");
        const initDataLoading = ref(true);
        const invocations = ref([]);
        const totalInvocationCount = ref(undefined);
        const currentPage = ref(0);
        const scrollableDiv = ref(null);
        const { currentUser } = storeToRefs(useUserStore());
        const allLoaded = computed(() => totalInvocationCount.value !== undefined && totalInvocationCount.value <= invocations.value.length);
        // check if we have scrolled to the top or bottom of the scrollable div
        const { arrived, scrollTop } = useAnimationFrameScroll(scrollableDiv);
        const isScrollable = ref(false);
        useAnimationFrameResizeObserver(scrollableDiv, ({ clientSize, scrollSize }) => {
            isScrollable.value = scrollSize.height >= clientSize.height + 1;
        });
        const scrolledTop = computed(() => !isScrollable.value || arrived.top);
        const scrolledBottom = computed(() => !isScrollable.value || arrived.bottom);
        onMounted(async () => {
            useInfiniteScroll(scrollableDiv.value, () => loadInvocations());
        });
        onUnmounted(() => {
            // Remove the infinite scrolling behavior
            useInfiniteScroll(scrollableDiv.value, () => { });
        });
        /** if screen size is as such that a scroller is not rendered,
         * we load enough invocations so that a scroller is rendered
         */
        watch(() => isScrollable.value, (scrollable) => {
            if (!scrollable && !allLoaded.value) {
                loadInvocations();
            }
        });
        const currentItemId = computed(() => {
            const path = route.path;
            const match = path.match(/\/workflows\/invocations\/([a-zA-Z0-9]+)/);
            return match ? match[1] : undefined;
        });
        const route = useRoute();
        const router = useRouter();
        function cardClicked(invocation) {
            if (props.inPanel) {
                emit("invocation-clicked");
            }
            router.push(`/workflows/invocations/${invocation.id}`);
        }
        function scrollToTop() {
            scrollableDiv.value?.scrollTo({ top: 0, behavior: "smooth" });
        }
        /**
         * Request invocations for the current user
         */
        async function loadInvocations() {
            if (currentUser.value && !currentUser.value.isAnonymous && !busy.value && !allLoaded.value) {
                busy.value = true;
                try {
                    const offset = props.limit * currentPage.value;
                    const extraProps = currentUser.value ? { user_id: currentUser.value.id } : {};
                    const [responseData, responseTotal] = await getData(offset, props.limit, "", "create_time", true, extraProps);
                    invocations.value = invocations.value.concat(responseData);
                    totalInvocationCount.value = responseTotal;
                    currentPage.value += 1;
                    errorMessage.value = "";
                }
                catch (e) {
                    errorMessage.value = `Failed to obtain invocations: ${e}`;
                }
                finally {
                    initDataLoading.value = false;
                    busy.value = false;
                }
            }
        }
        function historyName(historyId) {
            const historyStore = useHistoryStore();
            return historyStore.getHistoryNameById(historyId);
        }
        function stateClass(state) {
            if (stateClasses[state]) {
                return `node-header-invocation header-${stateClasses[state]}`;
            }
            return "";
        }
        function workflowName(workflowId) {
            const workflowStore = useWorkflowStore();
            return workflowStore.getStoredWorkflowNameByInstanceId(workflowId);
        }
        return { __sfc: true, props, emit, stateClasses, busy, errorMessage, initDataLoading, invocations, totalInvocationCount, currentPage, scrollableDiv, currentUser, allLoaded, arrived, scrollTop, isScrollable, scrolledTop, scrolledBottom, currentItemId, route, router, cardClicked, scrollToTop, loadInvocations, historyName, stateClass, workflowName, faEye, faArrowDown, faInfoCircle, FontAwesomeIcon, BAlert, BBadge, BButton, BListGroup, BListGroupItem, Heading, LoadingSpan, ScrollToTopButton, UtcDate };
    }
});
